import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ICON } from '@core/util/icon';
import { UserProfile } from '@models/userProfile';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessInviteService } from '@repos/businesses/users/business-invite.service';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { UserProfileState } from '@state/user-profile';
import LogRocket from 'logrocket';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { InviteModalComponent } from './invite-modal/invite-modal.component';
import { Invite } from '@models/businesses';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { BusinessDispatch, BusinessState } from '@state/business';

interface DataSourceInvite extends Invite {
	emailLowercase: string;
}

@UntilDestroy()
@Component({
	selector: 'app-invites',
	templateUrl: './invites.component.html',
	styleUrls: ['./invites.component.scss'],
	standalone: false,
})
export class InvitesComponent implements OnInit {
	@SelectSnapshot(BusinessState.GetRestrictedInvites) restricted: string;
	@SelectSnapshot(AuthState.getLoggedInUser) user: AuthUser;
	@SelectSnapshot(UserProfileState.getProfile) userProfile: UserProfile;

	public dataSource: MatTableDataSource<Invite> = new MatTableDataSource([]);
	public icon = ICON;
	public searchFilter = '';
	public loading = true;

	public displayedColumns: string[] = [
		'emailLowercase',
		'createdAt',
		'inviteLink',
	];

	constructor(
		private inviteService: BusinessInviteService,
		private dialog: MatDialog,
		private snackbarService: SnackbarService,
		private authenticationService: AuthenticationService,
		private businessDispatch: BusinessDispatch,
	) {}

	@ViewChild(MatSort)
	set sort(value: MatSort) {
		this.dataSource.sort = value;
	}

	ngOnInit(): void {
		this.getInvites();
	}

	public openInviteDialog(): void {
		this.dialog.open(InviteModalComponent);
	}

	getInvites = () => {
		this.inviteService
			.All()
			.pipe(untilDestroyed(this))
			.subscribe((data) => {
				this.dataSource.data = this.getDataSourceFromInvites(
					data as unknown as Invite[],
				);
				this.loading = false;
			});
	};

	applyFilter = (filterValue: string): void => {
		this.dataSource.filter = filterValue.trim().toLowerCase();
	};

	trackById(_index: number, item: Invite): string {
		return item.id;
	}

	resendInvite = async (inviteId: string, email: string) => {
		try {
			await this.authenticationService.resendInvite(inviteId).toPromise();

			this.snackbarService.openSnackBar({
				text: `Invite resent to ${email}.`,
				type: 'success',
			});
		} catch (err) {
			console.log('Unable to resend invite email', err);
			LogRocket.log('Unable to resend invite email', err);
			this.snackbarService.openSnackBar({
				text: 'Uh oh! We were unable to resend the invite. If problem persists please reach out',
				type: 'error',
			});
		}
	};

	deleteInvite = async (inviteId: string, email: string) => {
		try {
			await this.inviteService.Delete(inviteId);
			this.snackbarService.openSnackBar({
				text: `${email} invite deleted.`,
				type: 'success',
			});
		} catch (err) {
			LogRocket.log('Unable to delete invite ', err);
			this.snackbarService.openSnackBar({
				text: 'Uh oh! We were unable to delete the invite. If problem persists please reach out',
				type: 'error',
			});
		}
	};

	// Add emailLowercase property for sorting feature then sort invites by createdAt dates in desc order
	getDataSourceFromInvites(invites: Invite[]): DataSourceInvite[] {
		const sortedInvites: DataSourceInvite[] = invites
			.map((invite) => ({
				...invite,
				emailLowercase: invite.email.toLowerCase(),
			}))
			.sort((a, b) => {
				if (a.createdAt > b.createdAt) return -1;
				if (a.createdAt < b.createdAt) return 1;
				return 0;
			});

		return sortedInvites;
	}

	setBusinessRestricted = async (restricted: boolean): Promise<void> => {
		this.businessDispatch.OverwriteBusinessFields(
			'restrictedInvite',
			restricted,
		);
	};
}
